import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { notificationsAPI } from '../../services/api';
import { toAppPath } from '../../utils/notificationPaths';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);

  const countQuery = useQuery(
    ['notifications-unread-count'],
    async () => {
      const res = await notificationsAPI.getUnreadCount();
      return res.data?.data || { total: 0, needsAction: 0 };
    },
    { refetchInterval: 60000 }
  );

  const listQuery = useQuery(
    ['notifications-recent'],
    async () => {
      const res = await notificationsAPI.list({ limit: 8 });
      return res.data?.data || [];
    },
    { enabled: Boolean(anchorEl) }
  );

  const markReadMutation = useMutation(
    (id) => notificationsAPI.markRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications-unread-count']);
        queryClient.invalidateQueries(['notifications-recent']);
        queryClient.invalidateQueries(['notifications-important-updates']);
      },
    }
  );

  const counts = countQuery.data || { total: 0, needsAction: 0 };

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (item) => {
    if (!item.readAt) {
      try {
        await markReadMutation.mutateAsync(item.id);
      } catch (_) {}
    }
    handleClose();
    if (item.actionUrl) {
      const path = toAppPath(item.actionUrl);
      if (path) navigate(path);
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="Notifications">
        <Badge badgeContent={counts.total || 0} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 360, maxWidth: '95vw' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {counts.needsAction > 0 && (
            <Chip size="small" color="warning" label={`${counts.needsAction} need action`} />
          )}
        </Box>
        <Divider />
        {listQuery.isLoading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (listQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No notifications yet.
          </Typography>
        ) : (
          (listQuery.data || []).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => handleItemClick(item)}
              sx={{
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 0.5,
                bgcolor: item.isUnread ? 'action.hover' : 'transparent',
                whiteSpace: 'normal',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Typography variant="body2" fontWeight={item.isUnread ? 600 : 400} sx={{ flex: 1 }}>
                  {item.title}
                </Typography>
                {item.category === 'needs_action' && (
                  <Chip label="Action" size="small" color="warning" sx={{ height: 20 }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatWhen(item.createdAt)}
              </Typography>
            </MenuItem>
          ))
        )}
        <Divider />
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" onClick={() => { handleClose(); navigate('/notifications'); }}>
            View all
          </Button>
        </Box>
      </Menu>
    </>
  );
}
